import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, CircularProgress, Typography } from '@mui/material';
import { supabase } from '../../lib/supabase';
import ResetPasswordModal from '../../components/ResetPasswordModal/ResetPasswordModal';

/**
 * ResetPassword Page Component
 * 
 * Handles password reset flow when user clicks link from email.
 * Supabase automatically exchanges the token for a session when the link is clicked.
 * We check for the session and show password update modal.
 */
const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        // Check for password reset token in URL hash (Supabase uses hash for security)
        const hash = window.location.hash.substring(1);
        const hashParams = hash ? new URLSearchParams(hash) : new URLSearchParams();
        const accessToken = hashParams.get('access_token');
        const type = hashParams.get('type');

        // Also check query params (fallback)
        const queryToken = searchParams.get('access_token');
        const queryType = searchParams.get('type');

        // If we have a recovery token, Supabase will automatically exchange it for a session
        if ((accessToken && type === 'recovery') || (queryToken && queryType === 'recovery')) {
          // Wait a moment for Supabase to process the token exchange
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Check if we now have a session (token was exchanged)
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session) {
            // Session exists, show password update modal
            setShowModal(true);
          } else {
            // No session, token might be invalid or expired
            navigate('/', { replace: true });
          }
        } else {
          // No token found, redirect to home
          navigate('/', { replace: true });
        }
      } catch (err) {
        console.error('Error checking reset password token:', err);
        navigate('/', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, [navigate, searchParams]);

  const handleSuccess = () => {
    // After successful password update, redirect to login
    navigate('/login', { replace: true });
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress sx={{ color: '#00B4FF' }} />
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Verifying reset link...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Container maxWidth="sm">
        <ResetPasswordModal
          open={showModal}
          onClose={() => navigate('/', { replace: true })}
          onSuccess={handleSuccess}
          mode="update"
        />
      </Container>
    </Box>
  );
};

export default ResetPassword;

